import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown, Clock, DollarSign, BarChart2, RefreshCw, ChevronDown, ChevronUp, Maximize2 } from 'lucide-react';

const TeslaRealTimeChart = () => {
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('15m');
  const [expanded, setExpanded] = useState(false);
  const [currentValue, setCurrentValue] = useState(0);
  const [highValue, setHighValue] = useState(0);
  const [lowValue, setLowValue] = useState(0);
  const [changePercent, setChangePercent] = useState(0);
  const [visibleStats, setVisibleStats] = useState({
    price: true,
    volume: false,
    marketCap: false,
  });
  const [lastUpdateTime, setLastUpdateTime] = useState('');
  const updateIntervalRef = useRef(null);

  // Generate realistic Tesla-like stock price data
  const generateInitialData = (range) => {
    const now = new Date();
    let data = [];
    
    // Define time intervals and point counts based on range
    let pointCount = 0;
    let intervalMinutes = 0;
    let basePrice = 690.25; // Starting price point
    
    switch(range) {
      case '5m':
        pointCount = 60;
        intervalMinutes = 5/60;
        break;
      case '15m':
        pointCount = 60;
        intervalMinutes = 15/60;
        break;
      case '1h':
        pointCount = 60;
        intervalMinutes = 1;
        break;
      case '1d':
        pointCount = 96;
        intervalMinutes = 15;
        break;
      case '1w':
        pointCount = 84;
        intervalMinutes = 120;
        break;
      default:
        pointCount = 60;
        intervalMinutes = 15/60;
    }

    // Volatility increases with longer timeframes
    const volatilityMap = {
      '5m': 0.05,
      '15m': 0.08,
      '1h': 0.12,
      '1d': 0.5,
      '1w': 1.5
    };
    
    const volatility = volatilityMap[range];
    
    // Generate data points
    for (let i = 0; i < pointCount; i++) {
      const time = new Date(now);
      time.setMinutes(time.getMinutes() - (intervalMinutes * (pointCount - i)));
      
      // Realistic market behavior - more volatility with directional bias
      // More likely to follow previous direction with occasional reversals
      const direction = Math.random() > 0.45 ? 1 : -1;
      const change = direction * (Math.random() * volatility);
      
      // Percentage change application with some momentum
      basePrice = basePrice * (1 + change/100);
      
      // Add some volume data (higher at price changes)
      const volume = Math.round(50000 + Math.abs(change) * 50000 * Math.random());
      
      data.push({
        time: formatTime(time, range),
        value: basePrice,
        rawTime: time,
        volume: volume,
        timestamp: time.getTime()
      });
    }
    
    // Find high and low values
    const high = Math.max(...data.map(d => d.value));
    const low = Math.min(...data.map(d => d.value));
    
    // Calculate percent change
    const firstValue = data[0].value;
    const lastValue = data[data.length - 1].value;
    const percentChange = ((lastValue - firstValue) / firstValue) * 100;
    
    setHighValue(high);
    setLowValue(low);
    setCurrentValue(lastValue);
    setChangePercent(percentChange);
    setLastUpdateTime(formatTimeForDisplay(new Date()));
    
    return data;
  };

  // Format time based on range
  const formatTime = (date, range) => {
    if (range === '5m' || range === '15m' || range === '1h') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (range === '1d') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
    }
  };

  // Format time for last updated display
  const formatTimeForDisplay = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // Update chart with new data points in real-time
  const updateChartData = () => {
    setChartData(prevData => {
      if (prevData.length === 0) return prevData;
      
      const lastPoint = prevData[prevData.length - 1];
      const lastValue = lastPoint.value;
      
      // Generate a new data point that continues the trend with some randomness
      const now = new Date();
      const direction = Math.random() > 0.5 ? 1 : -1;
      
      // Smaller changes for real-time updates
      const change = direction * (Math.random() * 0.1);
      const newValue = lastValue * (1 + change/100);
      
      // Higher volume on bigger moves
      const volume = Math.round(50000 + Math.abs(change) * 100000 * Math.random());
      
      // Create a new point
      const newPoint = {
        time: formatTime(now, timeRange),
        value: newValue,
        rawTime: now,
        volume: volume,
        timestamp: now.getTime()
      };
      
      // Update states
      setCurrentValue(newValue);
      setHighValue(Math.max(highValue, newValue));
      setLowValue(Math.min(lowValue, newValue));
      setLastUpdateTime(formatTimeForDisplay(now));
      
      // Calculate percent change based on oldest visible point
      const firstValue = prevData[0].value;
      const percentChange = ((newValue - firstValue) / firstValue) * 100;
      setChangePercent(percentChange);
      
      // Return updated array with new point, removing oldest if we've hit our limit
      let newData = [...prevData, newPoint];
      
      // Keep array size consistent based on time range
      const maxPoints = {
        '5m': 60,
        '15m': 60,
        '1h': 60,
        '1d': 96,
        '1w': 84
      }[timeRange] || 60;
      
      if (newData.length > maxPoints) {
        newData = newData.slice(1);
      }
      
      return newData;
    });
  };

  // Initialize chart data based on selected time range
  useEffect(() => {
    setIsLoading(true);
    
    // Clear any existing interval
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
    }
    
    // Generate initial data
    setTimeout(() => {
      const initialData = generateInitialData(timeRange);
      setChartData(initialData);
      setIsLoading(false);
      
      // Set update interval - faster for shorter timeframes
      const updateInterval = {
        '5m': 1000,
        '15m': 2000,
        '1h': 3000,
        '1d': 5000,
        '1w': 8000
      }[timeRange] || 2000;
      
      updateIntervalRef.current = setInterval(updateChartData, updateInterval);
    }, 500);
    
    // Cleanup
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, [timeRange]);

  // Format values for display
  const formatPrice = (value) => {
    return value.toFixed(2);
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="tesla-chart-custom-tooltip">
          <p className="tooltip-time">{data.time}</p>
          <p className="tooltip-price">
            <span className="tooltip-label">Price:</span> 
            <span className="tooltip-value">KES {formatPrice(data.value)}</span>
          </p>
          <p className="tooltip-volume">
            <span className="tooltip-label">Volume:</span>
            <span className="tooltip-value">{Math.round(data.volume).toLocaleString()}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`tesla-realtime-chart-container ${expanded ? 'expanded' : ''}`}>
      <div className="tesla-realtime-chart-header">
        <div className="tesla-realtime-chart-title-section">
          <h3 className="tesla-realtime-chart-title">
            Tesla Investment Performance
            <span className="tesla-realtime-chart-title-tag">LIVE</span>
          </h3>
          <div className="tesla-realtime-chart-last-updated">
            <RefreshCw size={12} />
            <span>Last Updated: {lastUpdateTime}</span>
          </div>
        </div>
        
        <div className="tesla-realtime-chart-controls">
          <div className="tesla-realtime-chart-time-selector">
            {['5m', '15m', '1h', '1d', '1w'].map((range) => (
              <button
                key={range}
                className={`tesla-realtime-chart-time-button ${timeRange === range ? 'active' : ''}`}
                onClick={() => setTimeRange(range)}
              >
                {range}
              </button>
            ))}
          </div>
          
          <button 
            className="tesla-realtime-chart-expand-button"
            onClick={() => setExpanded(!expanded)}
          >
            <Maximize2 size={14} />
          </button>
        </div>
      </div>
      
      <div className="tesla-realtime-chart-stats">
        <div className="tesla-realtime-chart-price-stats">
          <div className="tesla-realtime-chart-current-price">
            <span className="tesla-realtime-chart-price-value">KES {formatPrice(currentValue)}</span>
            <span className={`tesla-realtime-chart-price-change ${changePercent >= 0 ? 'positive' : 'negative'}`}>
              {changePercent >= 0 ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {Math.abs(changePercent).toFixed(2)}%
            </span>
          </div>
          
          <div className="tesla-realtime-chart-price-range">
            <div className="tesla-realtime-chart-range-item">
              <span className="tesla-realtime-chart-range-label">H</span>
              <span className="tesla-realtime-chart-range-value">KES {formatPrice(highValue)}</span>
            </div>
            <div className="tesla-realtime-chart-range-item">
              <span className="tesla-realtime-chart-range-label">L</span>
              <span className="tesla-realtime-chart-range-value">KES {formatPrice(lowValue)}</span>
            </div>
          </div>
        </div>
        
        <div className="tesla-realtime-chart-indicators">
          <button
            className={`tesla-realtime-chart-indicator-button ${visibleStats.price ? 'active' : ''}`}
            onClick={() => setVisibleStats({...visibleStats, price: !visibleStats.price})}
          >
            <DollarSign size={14} />
            <span>Price</span>
          </button>
          <button
            className={`tesla-realtime-chart-indicator-button ${visibleStats.volume ? 'active' : ''}`}
            onClick={() => setVisibleStats({...visibleStats, volume: !visibleStats.volume})}
          >
            <BarChart2 size={14} />
            <span>Volume</span>
          </button>
        </div>
      </div>
      
      <div className="tesla-realtime-chart-content">
        {isLoading ? (
          <div className="tesla-realtime-chart-loading">
            <div className="tesla-realtime-chart-spinner"></div>
            <span>Loading market data...</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255, 255, 255, 0.05)" />
              <XAxis
                dataKey="time"
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                padding={{ left: 5, right: 5 }}
                minTickGap={30}
              />
              <YAxis
                domain={['dataMin - 1', 'dataMax + 1']}
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                tickFormatter={(value) => `${formatPrice(value)}`}
                orientation="right"
                width={50}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={chartData[0]?.value} stroke="rgba(255, 255, 255, 0.2)" strokeDasharray="3 3" />
              <Line
                type="monotone"
                dataKey="value"
                stroke={changePercent >= 0 ? '#4ade80' : '#f87171'}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: changePercent >= 0 ? '#4ade80' : '#f87171', strokeWidth: 2, fill: '#1e293b' }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      
      <style jsx>{`
        .tesla-realtime-chart-container {
          background: linear-gradient(135deg, #1e293b, #0f172a);
          border-radius: 12px;
          padding: 15px;
          margin-top: 20px;
          margin-bottom: 20px;
          border: 1px solid #334155;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          height: ${expanded ? '450px' : '320px'};
          transition: height 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        
        .tesla-realtime-chart-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }
        
        .tesla-realtime-chart-title-section {
          display: flex;
          flex-direction: column;
        }
        
        .tesla-realtime-chart-title {
          font-size: 16px;
          font-weight: 600;
          color: #e2e8f0;
          margin: 0 0 4px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .tesla-realtime-chart-title-tag {
          background: linear-gradient(135deg, #4ade80, #22c55e);
          color: white;
          font-size: 10px;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 12px;
          animation: tesla-chart-pulse 2s infinite;
        }
        
        @keyframes tesla-chart-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        .tesla-realtime-chart-last-updated {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 10px;
          color: #94a3b8;
        }
        
        .tesla-realtime-chart-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .tesla-realtime-chart-time-selector {
          display: flex;
          background: rgba(15, 23, 42, 0.5);
          border-radius: 6px;
          padding: 2px;
          border: 1px solid #334155;
        }
        
        .tesla-realtime-chart-time-button {
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 11px;
          padding: 4px 6px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-weight: 500;
        }
        
        .tesla-realtime-chart-time-button:hover {
          color: #e2e8f0;
        }
        
        .tesla-realtime-chart-time-button.active {
          background: #334155;
          color: #e2e8f0;
        }
        
        .tesla-realtime-chart-expand-button {
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid #334155;
          color: #94a3b8;
          width: 26px;
          height: 26px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .tesla-realtime-chart-expand-button:hover {
          color: #e2e8f0;
          background: #334155;
        }
        
        .tesla-realtime-chart-stats {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        
        .tesla-realtime-chart-price-stats {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .tesla-realtime-chart-current-price {
          display: flex;
          align-items: baseline;
          gap: 8px;
        }
        
        .tesla-realtime-chart-price-value {
          font-size: 18px;
          font-weight: 700;
          color: #e2e8f0;
        }
        
        .tesla-realtime-chart-price-change {
          display: flex;
          align-items: center;
          font-size: 14px;
          font-weight: 600;
        }
        
        .tesla-realtime-chart-price-change.positive {
          color: #4ade80;
        }
        
        .tesla-realtime-chart-price-change.negative {
          color: #f87171;
        }
        
        .tesla-realtime-chart-price-range {
          display: flex;
          gap: 12px;
        }
        
        .tesla-realtime-chart-range-item {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .tesla-realtime-chart-range-label {
          color: #94a3b8;
          font-size: 11px;
        }
        
        .tesla-realtime-chart-range-value {
          color: #e2e8f0;
          font-size: 12px;
        }
        
        .tesla-realtime-chart-indicators {
          display: flex;
          gap: 6px;
        }
        
        .tesla-realtime-chart-indicator-button {
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid #334155;
          color: #94a3b8;
          font-size: 11px;
          padding: 4px 8px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .tesla-realtime-chart-indicator-button:hover {
          color: #e2e8f0;
        }
        
        .tesla-realtime-chart-indicator-button.active {
          background: #334155;
          color: #e2e8f0;
        }
        
        .tesla-realtime-chart-content {
          position: relative;
          height: calc(100% - 95px);
          width: 100%;
        }
        
        .tesla-realtime-chart-loading {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: #94a3b8;
        }
        
        .tesla-realtime-chart-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid rgba(99, 102, 241, 0.1);
          border-top: 3px solid #6366f1;
          border-radius: 50%;
          animation: tesla-chart-spin 1s linear infinite;
        }
        
        @keyframes tesla-chart-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .tesla-chart-custom-tooltip {
          background: rgba(15, 23, 42, 0.9);
          border: 1px solid #334155;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          padding: 8px 10px;
          border-radius: 6px;
          font-size: 12px;
        }
        
        .tooltip-time {
          color: #e2e8f0;
          font-weight: 600;
          margin: 0 0 5px;
          padding-bottom: 4px;
          border-bottom: 1px solid #334155;
        }
        
        .tooltip-price, .tooltip-volume {
          margin: 3px 0;
          display: flex;
          justify-content: space-between;
          gap: 10px;
        }
        
        .tooltip-label {
          color: #94a3b8;
        }
        
        .tooltip-value {
          color: #e2e8f0;
          font-weight: 500;
        }
        
        /* Responsive styles */
        @media (max-width: 768px) {
          .tesla-realtime-chart-container {
            height: ${expanded ? '400px' : '260px'};
          }
          
          .tesla-realtime-chart-header {
            flex-direction: column;
            gap: 8px;
          }
          
          .tesla-realtime-chart-controls {
            width: 100%;
            justify-content: space-between;
          }
        }
        
        @media (max-width: 480px) {
          .tesla-realtime-chart-container {
            height: ${expanded ? '350px' : '220px'};
            padding: 10px;
          }
          
          .tesla-realtime-chart-stats {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          
          .tesla-realtime-chart-price-value {
            font-size: 16px;
          }
          
          .tesla-realtime-chart-content {
            height: calc(100% - 115px);
          }
        }
      `}</style>
    </div>
  );
};

export default TeslaRealTimeChart;